import React, { useState, useEffect } from 'react';
import { CategoryIcon, DownArrowIcon, MultiChoiceIcon, SubModelIcon, UpArrowIcon } from 'assests/GmsIcons';
import { useComponentRegistry } from '../../contexts/ComponentRegistryContext';
import { useEditor } from '../../contexts/EditorContext';

import { containerStore } from '../../utils/containerRegistryBridge';

type ComponentType = 'submodel' | 'field' | 'category' | 'connector';

type MenuItem = {
  id: string;
  name: string;
  type: ComponentType;
  parentId?: string;
  expanded?: boolean;
  isUsed: boolean;
  isDisabled: boolean;
}

type ModelsComponentProps = {
  id?: string;
  isCollapsed?: boolean;
  searchTerm?: string;
  selectedContainerId?: string;
  onAddComponent?: (componentId: string, componentType: string) => void;
  onAddComponentsBatch?: (submodelId: string, submodelType: string, childComponents: { id: string, type: string }[]) => void;
}

const ModelsComponent = ({ id, isCollapsed = false, searchTerm = '', selectedContainerId, onAddComponent, onAddComponentsBatch } : ModelsComponentProps) => {
  const baseId = id || 'models-component';
  const [submodels, setSubmodels] = useState<MenuItem[]>([]);
  const [childComponents, setChildComponents] = useState<MenuItem[]>([]);
  const [expandedSubmodels, setExpandedSubmodels] = useState<Record<string, boolean>>({});
  const { editorState } = useEditor();
  
  const { getAllComponentsWithUsageInfo, getUsageMap, getContainerSubmodelId, registryVersion} = useComponentRegistry();

  useEffect(() => {
    if (!selectedContainerId) {
      setSubmodels([]);
      setChildComponents([]);
      return;
    }
      
    const componentsWithInfo = getAllComponentsWithUsageInfo(selectedContainerId);
      
    const editorComponentUsage = editorState.componentUsage || {};
      
    const submodelItems = componentsWithInfo
      .filter(comp => comp.type === 'submodel')
      .map(submodel => {
        const isUsedInRegistry = submodel.usageStatus.isUsed;
        const isUsedInEditorState = !!editorComponentUsage[submodel.id]?.isUsed;
        const isUsed = isUsedInRegistry || isUsedInEditorState;

        const isDisabled = submodel.usageStatus.isDisabled || isUsed;
          
        return {
          id: submodel.id,
          name: submodel.name,
          type: submodel.type as ComponentType,
          isUsed: isUsed,
          isDisabled: isDisabled,
          expanded: expandedSubmodels[submodel.id] !== undefined 
            ? expandedSubmodels[submodel.id] 
            : !isCollapsed
          };
        });
      
      const childItems = componentsWithInfo
        .filter(comp => {
          if (comp.type === 'field' || comp.type === 'category' || comp.type === 'connector') {
            const isSelectedContainerNested = selectedContainerId ? 
              containerStore.isContainerNested(selectedContainerId) : false;
              
            if (isSelectedContainerNested && effectiveContainerSubmodelId) {
              return comp.parentId === effectiveContainerSubmodelId;
            }
            return true;
          }
          return false;
        })
        .map(child => {
          const isUsedInRegistry = child.usageStatus.isUsed;
          const isUsedInEditorState = !!editorComponentUsage[child.id]?.isUsed;
          const isUsed = isUsedInRegistry || isUsedInEditorState;
          
          let parentSubmodelUsed = false;
          if (child.parentId) {
            const parentUsedInRegistry = componentsWithInfo.find(
              c => c.id === child.parentId
            )?.usageStatus.isUsed || false;
            const parentUsedInEditorState = !!editorComponentUsage[child.parentId]?.isUsed;
            parentSubmodelUsed = parentUsedInRegistry || parentUsedInEditorState;
          }
          
          const isDisabled = child.usageStatus.isDisabled || isUsed || (parentSubmodelUsed && !isUsedInThisContainer(child.parentId, selectedContainerId));
          
          return {
            id: child.id,
            name: child.name,
            type: child.type as ComponentType,
            parentId: child.parentId,
            isUsed: isUsed,
            isDisabled: isDisabled
          };
        });
      
      setSubmodels(submodelItems);
      setChildComponents(childItems);
      
      const initialExpandedState: Record<string, boolean> = {};
      submodelItems.forEach(submodel => {
        if (expandedSubmodels[submodel.id] === undefined) {
          initialExpandedState[submodel.id] = !isCollapsed;
        }
      });
      
      if (Object.keys(initialExpandedState).length > 0) {
        setExpandedSubmodels(prev => ({
          ...prev,
          ...initialExpandedState
        }));
      }
      
  }, [ selectedContainerId, JSON.stringify(getUsageMap()), isCollapsed,editorState.componentUsage,JSON.stringify(editorState.componentUsage), registryVersion]);

  const isUsedInThisContainer = (submodelId: string | undefined, containerId: string): boolean => {
    if (!submodelId) return false;
    
    const registryUsage = getUsageMap()[submodelId];
    if (registryUsage?.isUsed && registryUsage.containerId === containerId) {
      return true;
    }
    
    const editorUsage = editorState.componentUsage?.[submodelId];
    if (editorUsage?.isUsed && editorUsage.containerId === containerId) {
      return true;
    }
    
    if (containerStore.isContainerNested(containerId)) {
    const rootContainerId = containerStore.getRootContainerId(containerId);
      
      const registryUsageRoot = getUsageMap()[submodelId];
      if (registryUsageRoot?.isUsed && registryUsageRoot.containerId === rootContainerId) {
        return true;
      }
      
      const editorUsageRoot = editorState.componentUsage?.[submodelId];
      if (editorUsageRoot?.isUsed && editorUsageRoot.containerId === rootContainerId) {
        return true;
      }
    }
    
    return false;
  };

  useEffect(() => {
    const showExpanded = () => {
      if (submodels.length > 0) {
        const forcedExpandState: Record<string, boolean> = {};
        submodels.forEach(submodel => {
          forcedExpandState[submodel.id] = true;
        });
        setExpandedSubmodels(forcedExpandState);
      }
    };
    
    showExpanded();
  }, [submodels.length, childComponents.length]);

  useEffect(() => {
    setExpandedSubmodels(prev => {
      const updated = { ...prev };
      submodels.forEach(submodel => {
        updated[submodel.id] = !isCollapsed;
      });
      return updated;
    });
  }, [isCollapsed, submodels]);

  const effectiveContainerId = selectedContainerId && containerStore.isContainerNested(selectedContainerId)
    ? containerStore.getRootContainerId(selectedContainerId) 
    : selectedContainerId;

  const effectiveContainerSubmodelId = effectiveContainerId 
    ? getContainerSubmodelId(effectiveContainerId) 
    : undefined;

  const getFilteredData = () => {
    if (!searchTerm) {
      return {
        filteredSubmodels: submodels,
        filteredChildComponents: childComponents
      };
    }

    const searchLower = searchTerm.toLowerCase();
    
    const matchingChildren = childComponents.filter(child => 
      child.name.toLowerCase().includes(searchLower)
    );
    
    const matchingSubmodels = submodels.filter(submodel => {
      const directMatch = submodel.name.toLowerCase().includes(searchLower);
      
      const hasMatchingChildren = matchingChildren.some(child => child.parentId === submodel.id);
      
      return directMatch || hasMatchingChildren;
    });
    
    return {
      filteredSubmodels: matchingSubmodels,
      filteredChildComponents: matchingChildren
    };
  };

  const { filteredSubmodels, filteredChildComponents } = getFilteredData();


  const isNestedContainerSelected = selectedContainerId 
    ? containerStore.isContainerNested(selectedContainerId) 
    : false;

  const toggleExpand = (submodelId: string) => {
    setExpandedSubmodels(prev => ({
      ...prev,
      [submodelId]: !prev[submodelId]
    }));
  };

  const handleSelect = (component: MenuItem, selectChildrenToo: boolean = false) => {
    if (component.isDisabled || !onAddComponent) {
      return;
    }

    if (component.type === 'submodel' && selectChildrenToo && onAddComponentsBatch) {
      // Find available children (not disabled, not used)
      const availableChildren = childComponents.filter(child => 
        child.parentId === component.id && !child.isDisabled && !child.isUsed
      );
      
      const componentsToAdd = availableChildren.map(child => ({
        id: child.id,
        type: child.type
      }));
      
      onAddComponentsBatch(component.id, component.type, componentsToAdd);
    } else {
      onAddComponent(component.id, component.type);
    }
  };

  const getComponentIcon = (component: MenuItem) => {
    if (component.type === 'category') {
      return <CategoryIcon />;
    }
    
    if (component.type === 'connector') {
      return (
        <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
    }
    
    const fieldName = component.name.toLowerCase();
    
    if (fieldName.includes('integer')) {
      return (
        <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
        </svg>
      );
    } else if (fieldName.includes('text')) {
      return (
        <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
        </svg>
      );
    } else if (fieldName.includes('decimal')) {
      return (
        <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
        </svg>
      );
    } else if (fieldName.includes('fraction')) {
      return (
        <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 5h14M5 19h14" />
        </svg>
      );
    } else if (fieldName.includes('boolean') || fieldName.includes('bool')) {
      return (
        <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    } else if (fieldName.includes('range')) {
      return (
        <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      );
    } else if (
      fieldName.includes('multichoice') || 
      fieldName.includes('multi-choice')
    ) {
      return <MultiChoiceIcon />;
    }
    
    return (
      <svg className="tw-w-4 tw-h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
      </svg>
    );
  };

  if (!selectedContainerId) {
    return (
      <div id={`${baseId}-no-container`} className="tw-w-full tw-bg-[#F4F5F7] tw-border tw-border-[#EFEFF0] tw-p-4 tw-text-center tw-text-gray-500">
        Select a container to see available components
      </div>
    );
  }

  if (submodels.length === 0) {
    return (
      <div id={`${baseId}-no-components`} className="tw-w-full tw-bg-[#F4F5F7] tw-border tw-border-[#EFEFF0] tw-p-4 tw-text-center tw-text-gray-500">
        No components available for this container
      </div>
    );
  }

  if (submodels.length === 0 && !isNestedContainerSelected) {
    return (
      <div id={`${baseId}-no-components-alt`} className="tw-w-full tw-bg-[#F4F5F7] tw-border tw-border-[#EFEFF0] tw-p-4 tw-text-center tw-text-gray-500">
        No components available for this container
      </div>
    );
  }

  if (searchTerm && filteredSubmodels.length === 0 && filteredChildComponents.length === 0) {
    return (
      <div id={`${baseId}-no-search-results`} className="tw-w-full tw-bg-[#F4F5F7] tw-border tw-border-[#EFEFF0] tw-p-4 tw-text-center tw-text-gray-500">
        {`No components found matching "${searchTerm}"`}
      </div>
    );
  }

  return (
    <div id={baseId} data-testid={baseId} className="tw-w-full tw-bg-[#F4F5F7] tw-border tw-border-[#EFEFF0]">
      {isNestedContainerSelected && (
        <div id={`${baseId}-nested-note`} className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded tw-p-3 tw-mb-2 tw-mx-2">
          <div id={`${baseId}-nested-note-text`} className="tw-text-sm tw-text-blue-700">
            <span className="tw-font-medium">Note:</span> Nested containers inherit fields from their parent container's submodel.
          </div>
        </div>
      )}
      
      {filteredSubmodels.map(submodel => {
        const submodelChildren = filteredChildComponents.filter(child => child.parentId === submodel.id);
        const isExpanded = expandedSubmodels[submodel.id] || false;

        return (
          <div key={submodel.id} id={`${baseId}-submodel-${submodel.id}`}>
            {/* Submodel row */}
            <div
              id={`${baseId}-submodel-${submodel.id}-row`}
              className={`tw-flex tw-items-center tw-px-4 tw-py-2 tw-border-b tw-border-[#DADBDD] ${
                submodel.isDisabled ? 'tw-bg-gray-100 tw-cursor-not-allowed' : 'tw-cursor-pointer'
              }`}
              onClick={() => !isNestedContainerSelected && toggleExpand(submodel.id)}
            >
              {/* Checkbox for submodel selection */}
              <div id={`${baseId}-submodel-${submodel.id}-checkbox-wrapper`} className="tw-flex tw-items-center tw-mr-3">
                <input
                  id={`${baseId}-submodel-${submodel.id}-checkbox`}
                  type="checkbox"
                  className="tw-rounded tw-border-gray-300 tw-text-blue-600 tw-focus:ring-blue-500"
                  disabled={submodel.isDisabled || isNestedContainerSelected}
                  checked={submodel.isUsed}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (!submodel.isUsed && !isNestedContainerSelected) {
                      handleSelect(submodel, true);
                    }
                  }}
                />
              </div>

              {/* Submodel label with icon */}
              <div id={`${baseId}-submodel-${submodel.id}-label`} className={`tw-flex tw-items-center tw-flex-1 ${submodel.isDisabled ? 'tw-opacity-50' : ''}`}>
                <SubModelIcon />
                <span className="tw-ml-2 tw-text-sm">{submodel.name}</span>
              </div>

              {/* Expand/collapse icon */}
              <div id={`${baseId}-submodel-${submodel.id}-toggle`} className="tw-ml-auto">
                {isExpanded ? <UpArrowIcon /> : <DownArrowIcon />}
              </div>
            </div>
            
            {/* Child component rows when submodel is expanded */}
            {isExpanded && submodelChildren.length > 0 && (
              <div id={`${baseId}-submodel-${submodel.id}-children`}>
                {submodelChildren.map(child => (
                  <div
                    key={child.id}
                    id={`${baseId}-child-${child.id}`}
                    className={`tw-flex tw-items-center tw-px-4 tw-py-2 tw-border-b tw-border-[#DADBDD] tw-pl-10 ${
                      child.isDisabled ? 'tw-bg-gray-100 tw-cursor-not-allowed' : 'tw-cursor-pointer'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!child.isDisabled && !child.isUsed) {
                        handleSelect(child, false);
                      }
                    }}
                  >
                    {/* Checkbox for child component selection */}
                    <div id={`${baseId}-child-${child.id}-checkbox-wrapper`} className="tw-flex tw-items-center tw-mr-3">
                      <input
                        id={`${baseId}-child-${child.id}-checkbox`}
                        type="checkbox"
                        className="tw-rounded tw-border-gray-300 tw-text-blue-600 tw-focus:ring-blue-500"
                        disabled={child.isDisabled}
                        checked={child.isUsed}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (!child.isUsed && !child.isDisabled) {
                            handleSelect(child, false);
                          }
                        }}
                      />
                    </div>

                    {/* Child component label with appropriate icon */}
                    <div id={`${baseId}-child-${child.id}-label`} className={`tw-flex tw-items-center ${child.isDisabled ? 'tw-opacity-50' : ''}`}>
                      {getComponentIcon(child)}
                      <span className="tw-ml-2 tw-text-sm">{child.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty state when submodel has no child components */}
            {isExpanded && submodelChildren.length === 0 && !searchTerm && (
              <div id={`${baseId}-submodel-${submodel.id}-no-fields`} className="tw-px-10 tw-py-2 tw-border-b tw-border-[#DADBDD] tw-text-gray-500 tw-text-sm">
                No fields available for this submodel
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ModelsComponent;